<template>
  <div class="product-list pa-6">

    <!-- Header -->
    <div class="list-header mb-5">
      <div>
        <span v-if="!loading" class="results-label grey--text text--darken-1 body-2">
          <strong>{{ activeProducts.length }}</strong> service{{ activeProducts.length !== 1 ? 's' : '' }}
        </span>
      </div>
      <v-btn color="#525A68" rounded dark elevation="0" class="create-btn"
        @click="$router.push({ name: '/supplier-setup/create-product', params: { supplier_id: supplierId } })">
        <v-icon left small>mdi-plus</v-icon>
        Add Service
      </v-btn>
    </div>

    <!-- Loading state -->
    <v-row v-if="loading">
      <v-col v-for="n in 3" :key="'skeleton-' + n" cols="12" sm="6" lg="4">
        <v-skeleton-loader type="image, article" class="product-card"></v-skeleton-loader>
      </v-col>
    </v-row>

    <!-- Empty state -->
    <div v-else-if="activeProducts.length === 0" class="empty-state">
      <v-icon size="60" color="#E0E0E0" class="mb-3">mdi-briefcase-outline</v-icon>
      <div class="grey--text text--darken-1 body-2">No services yet</div>
      <v-btn text color="primary" class="mt-3"
        @click="$router.push({ name: '/supplier-setup/create-product', params: { supplier_id: supplierId } })">
        Add your first service
      </v-btn>
    </div>

    <!-- Product cards -->
    <v-row v-else>
      <v-col v-for="(item, index) in activeProducts" :key="index" cols="12" sm="6" lg="4">
        <v-card class="product-card" elevation="0" @click="selectProduct(item)">
          <!-- Thumbnail -->
          <div class="product-thumb">
            <img v-if="item.image" :src="api_url + 'media/suppliers/images/' + item.image" class="product-img" />
            <div v-else class="product-img-placeholder">
              <v-icon size="40" color="#C8C8C8">mdi-image-off-outline</v-icon>
            </div>
          </div>

          <v-card-text class="product-body">
            <!-- Name + type -->
            <div class="product-name-row mb-1">
              <a :href="$router.resolve({ name: '/supplier-setup/product/setup', params: { supplier_id: supplierId, product_id: item.id } }).href"
                class="product-name" @click.prevent.stop="selectProduct(item)">{{ item.name }}</a>
              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" @click.stop class="ml-2">
                    <v-icon color="#9E9E9E">mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click.stop="duplicateProduct(item)">
                    <v-list-item-icon class="mr-2">
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Duplicate</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click.stop="deleteProduct(item)">
                    <v-list-item-icon class="mr-2">
                      <v-icon small color="error">mdi-delete</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="error--text">Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
            <v-chip v-for="(name, i) in productTypeNames(item)" :key="i" x-small label class="type-chip mb-3 mr-1">
              {{ name }}
            </v-chip>
            <v-chip
              x-small label
              class="doc-count-chip mb-3 ml-2"
              :color="item.documents_count > 0 ? 'primary' : '#E0E0E0'"
              :text-color="item.documents_count > 0 ? 'white' : '#9E9E9E'"
            >
              {{ item.documents_count || 0 }} document{{ item.documents_count === 1 ? '' : 's' }}
            </v-chip>

            <v-divider class="mb-3" />

            <!-- Pricing row -->
            <div class="pricing-row">
              <div class="price-block">
                <div class="price-label">Customer</div>
                <div class="price-value">
                  {{ get_selected_company.currency }}{{ formatPrice(item.customer_price) }}
                </div>
              </div>
              <div class="price-divider" />
              <div class="price-block">
                <div class="price-label">Supplier</div>
                <div class="price-value supplier">
                  {{ get_selected_company.currency }}{{ formatPrice(item.supplier_price) }}<span v-if="item.supplier_price_type == 1" class="price-label"> /group</span>
                </div>
              </div>
              <div class="price-divider" />
              <div class="price-block">
                <div class="price-label">Type</div>
                <div class="price-value text-capitalize">
                  {{ productTypeNames(item).length ? productTypeNames(item).join(', ') : '—' }}
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Archived section -->
    <div v-if="archivedProducts.length > 0" class="mt-8">
      <v-btn text small color="grey" class="mb-3" @click="showArchived = !showArchived">
        <v-icon left small>{{ showArchived ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        {{ archivedProducts.length }} archived service{{ archivedProducts.length !== 1 ? 's' : '' }}
      </v-btn>

      <v-row v-if="showArchived">
        <v-col v-for="(item, index) in archivedProducts" :key="'arch-' + index" cols="12" sm="6" lg="4">
          <v-card class="product-card archived-card" elevation="0" @click="selectProduct(item)">
            <div class="product-thumb">
              <img v-if="item.image" :src="api_url + 'media/suppliers/images/' + item.image" class="product-img"
                style="opacity:0.5" />
              <div v-else class="product-img-placeholder">
                <v-icon size="40" color="#C8C8C8">mdi-image-off-outline</v-icon>
              </div>
              <v-chip x-small label class="archived-badge">Archived</v-chip>
            </div>
            <v-card-text class="product-body">
              <div class="product-name-row mb-1">
                <a :href="$router.resolve({ name: '/supplier-setup/product/setup', params: { supplier_id: supplierId, product_id: item.id } }).href"
                  class="product-name grey--text" @click.prevent.stop="selectProduct(item)">{{ item.name }}</a>
                <v-menu offset-y left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" @click.stop class="ml-2">
                      <v-icon color="#9E9E9E">mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click.stop="duplicateProduct(item)">
                      <v-list-item-icon class="mr-2">
                        <v-icon small>mdi-content-copy</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Duplicate</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click.stop="restoreProduct(item)">
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="success">mdi-restore</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title class="success--text">Restore</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <v-chip v-for="(name, i) in productTypeNames(item)" :key="i" x-small label class="type-chip mb-3 mr-1">
                {{ name }}
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
export default {
  props: ['supplierId'],
  data: () => ({
    showArchived: false,
    api_url: null,
    products: [],
    loading: true,
  }),
  async mounted() {
    this.api_url = process.env.VUE_APP_API_URL
    await this.fetchProducts()
    await this.$store.dispatch('supplier/fetch_supplier_type')
  },
  computed: {
    ...mapGetters({
      get_selected_company: 'auth/get_selected_company',
      get_supplier_type: 'supplier/get_supplier_type',
    }),
    activeProducts() {
      return this.products.filter(x => !x.deleted_at)
    },
    archivedProducts() {
      return this.products.filter(x => !!x.deleted_at)
    },
  },
  methods: {
    async fetchProducts() {
      this.loading = true
      const { data } = await this.$axios.get(`admin/suppliers/get_supplier_products/${this.supplierId}`)
      if (data.response) {
        this.products = data.data
      }
      this.loading = false
    },
    selectProduct(item) {
      this.$router.push({
        name: '/supplier-setup/product/setup',
        params: { supplier_id: this.supplierId, product_id: item.id },
      })
    },
    formatPrice(val) {
      if (val == null || val === '') return '—'
      return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    productTypeNames(item) {
      const ids = (item.service_type_ids && item.service_type_ids.length) ? item.service_type_ids : []
      const names = ids.map((id) => this.get_supplier_type.find((t) => t.id === id)?.name).filter(Boolean)
      if (names.length) return names
      return item.product_type ? [item.product_type.name] : []
    },
    async duplicateProduct(product) {
      if (!confirm(`Duplicate "${product.name}"?`)) return
      try {
        const { data } = await this.$axios.post(`admin/suppliers/duplicate_product/${product.id}`)
        if (data.response) {
          this.$toast.success(data.message || 'Product duplicated successfully')
          await this.fetchProducts()
        } else {
          this.$toast.error(data.message || 'Failed to duplicate product')
        }
      } catch (error) {
        console.error('Duplicate error:', error)
        this.$toast.error('Error duplicating product')
      }
    },
    async deleteProduct(product) {
      if (!confirm(`Delete "${product.name}"? This will archive the service.`)) return
      try {
        const { data } = await this.$axios.delete(`admin/suppliers/delete_product/${product.id}`)
        if (data.response) {
          this.$toast.success(data.message || 'Product deleted successfully')
          await this.fetchProducts()
        } else {
          this.$toast.error(data.message || 'Failed to delete product')
        }
      } catch (error) {
        console.error('Delete error:', error)
        this.$toast.error('Error deleting product')
      }
    },
    async restoreProduct(product) {
      if (!confirm(`Restore "${product.name}"?`)) return
      try {
        const { data } = await this.$axios.post(`admin/suppliers/restore_product/${product.id}`)
        if (data.response) {
          this.$toast.success(data.message || 'Product restored successfully')
          await this.fetchProducts()
        } else {
          this.$toast.error(data.message || 'Failed to restore product')
        }
      } catch (error) {
        console.error('Restore error:', error)
        this.$toast.error('Error restoring product')
      }
    },
  },
}
</script>

<style scoped lang="scss">
.product-list {
  background-color: #F5F6FA;
  min-height: 100%;
}

/* Header */
.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.create-btn {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
  height: 36px;
  padding: 0 18px;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  text-align: center;
}

/* Product card */
.product-card {
  border-radius: 12px !important;
  border: 1px solid #E8E8E8 !important;
  background-color: #fff;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.09) !important;
    transform: translateY(-1px);
  }
}

.archived-card {
  opacity: 0.75;
}

/* Thumbnail */
.product-thumb {
  position: relative;
  width: 100%;
  height: 150px;
  background-color: #F5F5F5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.product-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-img-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.archived-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: #FAFAFA !important;
  color: #9E9E9E !important;
  border: 1px solid #E0E0E0 !important;
  font-weight: 600;
}

/* Card body */
.product-body {
  padding: 14px 16px 12px !important;
}

.product-name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.product-name {
  font-size: 16px;
  font-weight: 700;
  color: #3988AA;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  text-decoration: underline;
  display: inline-block;
}

.type-chip {
  background-color: #F0F4FF !important;
  color: #3D5AFE !important;
  font-size: 11px !important;
  font-weight: 600;
  border-radius: 5px !important;
}

.doc-count-chip {
  font-size: 11px !important;
  font-weight: 600;
  border-radius: 5px !important;
}

/* Pricing */
.pricing-row {
  display: flex;
  align-items: flex-start;
  gap: 0;
}

.price-block {
  flex: 1;
  text-align: center;
}

.price-divider {
  width: 1px;
  background-color: #F0F0F0;
  align-self: stretch;
  margin: 0 6px;
}

.price-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #9E9E9E;
  margin-bottom: 2px;
}

.price-value {
  font-size: 13px;
  font-weight: 700;
  color: #2C2C2C;

  &.supplier {
    color: #669E53;
  }
}
</style>
